import React from 'react';
import dayjs from 'dayjs';
import { I18n } from 'react-redux-i18n';

import { ViewTable } from '../../../../components/layouts/viewTable';
import { ViewSectionRow } from '../../../../components/layouts/viewSectionRow';
import currencyHelper from '../../../../helpers/currencyHelper';
import config from '../../../../constants';

import {
  actionableRefusalReasons,
  GIFT_AID_I18N_KEY,
  GiftAidClaim,
  giftAidClaimAttention,
  giftAidClaimStatus
} from './constants';

const knownAttentionCodes: readonly string[] = Object.values(
  giftAidClaimAttention
);

// A code core adds later still gets a message rather than a raw key.
export const getAttentionMessage = (claim: GiftAidClaim) => {
  const attention = claim.attention;
  if (!attention) return null;

  const code = knownAttentionCodes.includes(attention) ? attention : 'generic';
  return I18n.t(`${GIFT_AID_I18N_KEY}.attention.${code}`);
};

interface DonationGiftAidProps {
  donation: {
    currency?: string;
    donor?: { giftAidClaim?: GiftAidClaim | null } | null;
  };
  isShowEmptyFields?: boolean;
}

const formatDate = (value: string | null) =>
  value
    ? dayjs(value).format(config.INVERSION_FULL_DATE_TIME_PERIODS_FORMAT)
    : null;

export const getRefusalMessage = (claim: GiftAidClaim) => {
  if (claim.status !== giftAidClaimStatus.refused) return null;

  const reason = claim.refusalReason;
  if (reason && actionableRefusalReasons.includes(reason)) {
    return I18n.t(`${GIFT_AID_I18N_KEY}.refusal-reason.${reason}`);
  }
  return I18n.t(`${GIFT_AID_I18N_KEY}.refusal-reason.generic`);
};

export const DonationGiftAid = ({
  donation,
  isShowEmptyFields
}: DonationGiftAidProps) => {
  const claim = donation.donor?.giftAidClaim;
  if (!claim) return null;

  const amount =
    claim.amount === null || claim.amount === undefined
      ? null
      : currencyHelper.formatCurrency(claim.amount, donation.currency, {
          alwaysShowDecimals: true
        });

  return (
    <div className="donation-gift-aid">
      <ViewTable title={I18n.t(`${GIFT_AID_I18N_KEY}.title`)}>
        <ViewSectionRow
          title={`${GIFT_AID_I18N_KEY}.status-label`}
          data={I18n.t(`${GIFT_AID_I18N_KEY}.status.${claim.status}`)}
        />
        <ViewSectionRow
          title={`${GIFT_AID_I18N_KEY}.refusal-reason-label`}
          data={getRefusalMessage(claim)}
        />
        <ViewSectionRow
          title={`${GIFT_AID_I18N_KEY}.attention-label`}
          data={getAttentionMessage(claim)}
        />
        <ViewSectionRow
          title={`${GIFT_AID_I18N_KEY}.attention-at`}
          data={
            claim.attention ? formatDate(claim.attentionAtLocal ?? null) : null
          }
        />
        <ViewSectionRow
          title={`${GIFT_AID_I18N_KEY}.amount`}
          data={amount}
          isShowEmptyFields={isShowEmptyFields}
        />
        <ViewSectionRow
          title={`${GIFT_AID_I18N_KEY}.declared-at`}
          data={formatDate(claim.declaredAtLocal)}
          isShowEmptyFields={isShowEmptyFields}
        />
        <ViewSectionRow
          title={`${GIFT_AID_I18N_KEY}.filed-at`}
          data={formatDate(claim.filedAtLocal)}
          isShowEmptyFields={isShowEmptyFields}
        />
        <ViewSectionRow
          title={`${GIFT_AID_I18N_KEY}.settlement-sent-at`}
          data={formatDate(claim.settlementSentAtLocal)}
          isShowEmptyFields={isShowEmptyFields}
        />
        <ViewSectionRow
          title={`${GIFT_AID_I18N_KEY}.refused-at`}
          data={formatDate(claim.refusedAtLocal)}
          isShowEmptyFields={isShowEmptyFields}
        />
        <ViewSectionRow
          title={`${GIFT_AID_I18N_KEY}.cancelled-at`}
          data={formatDate(claim.cancelledAtLocal)}
          isShowEmptyFields={isShowEmptyFields}
        />
      </ViewTable>
    </div>
  );
};
